import { CodedProperty, ensure, mapToCodedProperty, mapToNamedProperty } from "sprint-common-web";

import { GetOnPremAssetDetailsResponse } from "../../../common/api/master-api/assets/details/GetOnPremAssetDetailsResponse";
import { AspectRatio } from "../../../common/data/lookup-data-providers/AspectRatiosDataProvider";
import { AspectType } from "../../../common/data/lookup-data-providers/AspectTypesDataProvider";
import { ContentDetails } from "../../../common/types/ContentDetails";
import { DynamicSubtitleType, mapNumberToDynamicSubtitleType } from "../../../common/types/DynamicSubtitleType";
import { RecordType } from "../../../common/types/RecordType";
import { Standard } from "../../../common/types/Standard";
import { OnPremAssetStatus, mapToOnPremAssetStatus } from "./OnPremAssetStatus";

export enum OnPremAssetDetailsTab {
    Info = 1,
    Assets = 2
}

export type OnPremAssetDetails = OnPremVideoDetails | OnPremAudioDetails | OnPremSubtitleDetails;

interface BaseOnPremAssetDetails {
    assetId: string;
    fileRecordReference: OnPremFileRecordReference;
    originator?: string;
    status: OnPremAssetStatus;
    content: ContentDetails;
    isTrailer: boolean;
}

export interface OnPremVideoDetails extends BaseOnPremAssetDetails {
    type: RecordType.Video;
    technical: OnPremVideoTechnical;
    subtitle: CodedProperty;
}

export interface OnPremAudioDetails extends BaseOnPremAssetDetails {
    type: RecordType.Audio;
    technical: OnPremAudioTechnical;
    soundtrack: CodedProperty;
}

export interface OnPremSubtitleDetails extends BaseOnPremAssetDetails {
    type: RecordType.Subtitle;
    subtitle: CodedProperty;
    subtitleType: DynamicSubtitleType;
}

export interface OnPremVideoTechnical {
    aspectRatio: AspectRatio;
    aspectType?: AspectType;
    encoding: boolean;
    videoBitRate?: number;
    duration: string;
    resolution?: string;
    standard?: Standard;
    framerate?: string;
}

export interface OnPremAudioTechnical {
    resolution?: string;
    audioFormat?: string;
    duration?: string;
    sampleRate?: number;
    channelsCount?: number;
    audioBitRate?: number;
}

export type OnPremFileRecordReference = {
    id: string;
    filePath?: string;
    fileName: string;
    fileExtension: string;
    arrivalDate: Date;
    ingestedDate: Date;
};

export const isOnPremAssetDeprecated = (asset: OnPremAssetDetails): boolean =>
    asset.status === OnPremAssetStatus.Deprecated;

export const mapToOnPremAssetDetails = (res: GetOnPremAssetDetailsResponse): OnPremAssetDetails => {
    switch (res.type) {
        case RecordType.Video: {
            return {
                ...mapCommonDetails(res),
                type: res.type,
                subtitle: mapToCodedProperty(res.subtitle),
                technical: {
                    aspectRatio: mapToNamedProperty(res.technical.aspectRatio),
                    aspectType: res.technical.aspectType && mapToNamedProperty(res.technical.aspectType),
                    encoding: res.technical.encoding,
                    videoBitRate: res.technical.videoBitRate,
                    duration: res.technical.duration,
                    resolution: res.technical.resolution,
                    standard: res.technical.standard && mapToNamedProperty(res.technical.standard),
                    framerate: res.technical.framerate
                }
            };
        }

        case RecordType.Audio: {
            return {
                ...mapCommonDetails(res),
                type: res.type,
                soundtrack: mapToCodedProperty(res.soundtrack),
                technical: {
                    duration: res.technical.duration,
                    resolution: res.technical.resolution,
                    sampleRate: res.technical.sampleRate,
                    channelsCount: res.technical.channels,
                    audioBitRate: res.technical.bitRate,
                    audioFormat: res.technical.audioFormat
                }
            };
        }

        case RecordType.Subtitle: {
            return {
                ...mapCommonDetails(res),
                type: res.type,
                subtitle: mapToCodedProperty(res.subtitle),
                subtitleType: mapNumberToDynamicSubtitleType(res.subtitleType)
            };
        }
    }
};

const mapCommonDetails = (res: GetOnPremAssetDetailsResponse) => {
    return {
        assetId: res.id,
        status: mapToOnPremAssetStatus(res.status),
        fileRecordReference: {
            id: res.file.fileRecordId,
            arrivalDate: new Date(res.file.arrivalDate),
            ingestedDate: new Date(res.file.ingestedDate),
            filePath: res.file.path,
            fileName: res.file.name,
            fileExtension: res.file.extension
        },
        originator: res.originator?.name,
        content: res.content,
        isTrailer: res.isTrailer
    };
};

export const ensureOnPremVideoDetails: (details?: OnPremAssetDetails) => asserts details is OnPremVideoDetails = (
    details?: OnPremAssetDetails
) => {
    ensure(details);

    if (details.type !== RecordType.Video) {
        throw Error(`Asset record type should be video'`);
    }
};

export const ensureOnPremAudioDetails: (details?: OnPremAssetDetails) => asserts details is OnPremAudioDetails = (
    details?: OnPremAssetDetails
) => {
    ensure(details);

    if (details.type !== RecordType.Audio) {
        throw Error(`Asset record type should be audio'`);
    }
};

export const ensureOnPremSubtitleDetails: (details?: OnPremAssetDetails) => asserts details is OnPremSubtitleDetails = (
    details?: OnPremAssetDetails
) => {
    ensure(details);

    if (details.type !== RecordType.Subtitle) {
        throw Error(`Asset record type should be subtitle'`);
    }
};
